import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Search, Check, Building2, MapPin, Loader2, Sparkles, Star, AlertTriangle, ExternalLink,
} from 'lucide-react'
import {
  Modal, Button, Field, TextInput, TextArea, Select, FormGrid,
} from '@/components/ui'
import { useNavigate } from 'react-router-dom'
import { useStore, useHasPermission } from '@/store/useStore'
import {
  CONTACT_TYPE_OPTIONS, CONTACT_STATUS_OPTIONS, HOLIDAY_OPTIONS,
} from '@/types/entities'
import type {
  Contact, ContactType, ContactStatus, Holiday, InfluenceLevel, Stance, Address, Social,
  Account, AccountType, DealContactRole, RelationshipTrack,
} from '@/types/entities'
import { UNIVERSITIES } from '@/lib/universities'
import { contactName } from '@/lib/selectors'
import { findExistingContacts } from '@/lib/contactMatch'
import './ContactForm.css'

const STANCES: Stance[] = ['Champion', 'Ally', 'Neutral', 'Skeptic', 'Opponent']
const INFLUENCES: InfluenceLevel[] = ['High', 'Medium', 'Low']
const REL_ROLES: DealContactRole[] = ['Primary Contact', 'Decision Maker', 'Champion', 'Influencer']

/* ---- phone: format to (xxx) xxx-xxxx (with optional +1) as the user types ---- */
function formatPhone(raw: string): string {
  let d = raw.replace(/\D/g, '')
  let prefix = ''
  if (d.length > 10 && d.startsWith('1')) { prefix = '+1 '; d = d.slice(1, 11) } else d = d.slice(0, 10)
  if (!d) return raw.trim().startsWith('+') ? '+' : ''
  if (d.length < 4) return `${prefix}(${d}`
  if (d.length < 7) return `${prefix}(${d.slice(0, 3)}) ${d.slice(3)}`
  return `${prefix}(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`
}

/* ---- socials: store full URL, edit just the handle behind a fixed prefix ---- */
const SOCIALS: { key: keyof Social; label: string; prefix: string }[] = [
  { key: 'linkedIn', label: 'LinkedIn', prefix: 'linkedin.com/in/' },
  { key: 'instagram', label: 'Instagram', prefix: 'instagram.com/' },
  { key: 'facebook', label: 'Facebook', prefix: 'facebook.com/' },
  { key: 'tiktok', label: 'TikTok', prefix: 'tiktok.com/@' },
  { key: 'youtube', label: 'YouTube', prefix: 'youtube.com/@' },
]
function toHandle(prefix: string, url?: string): string {
  if (!url) return ''
  return url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(prefix, '').replace(/^@/, '').replace(/\/$/, '')
}
function toUrl(prefix: string, handle: string): string {
  const h = handle.trim().replace(/^@/, '')
  if (!h) return ''
  if (/^https?:\/\//i.test(handle)) return handle.trim()
  return `https://${prefix}${h}`
}

const STATE_ABBR: Record<string, string> = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA', Colorado: 'CO',
  Connecticut: 'CT', Delaware: 'DE', 'District of Columbia': 'DC', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY',
  Louisiana: 'LA', Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN',
  Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH',
  'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND',
  Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI',
  'South Carolina': 'SC', 'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
}
const abbr = (s?: string) => (s ? STATE_ABBR[s] ?? s : '')

function slugify(s: string) { return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') }
/** Id equality the way the rest of the platform does it: both sides lower-
 *  cased (account ids are uuids, and the API has served them in both cases). */
const sameId = (a?: string | null, b?: string | null): boolean =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()
const CONTACT_TO_ACCOUNT_TYPE: Partial<Record<ContactType, AccountType>> = {
  Investor: 'Investor', '3PM': '3PM', Broker: 'Broker', Vendor: 'Vendor', JV: 'JV', Municipal: 'Municipal/Government',
}

type AcctMode = 'none' | 'existing' | 'new'
type Draft = Partial<Contact> & { _child1?: string; _child2?: string }

function toDraft(c?: Contact): Draft {
  if (!c) return { firstName: '', lastName: '', type: 'Investor', status: 'Prospect', social: {}, officeAddress: {}, trackMemberships: [] }
  return { ...c, _child1: c.children?.[0] ?? '', _child2: c.children?.[1] ?? '' }
}

/* ---- Nominatim (OpenStreetMap) address suggestion ---- */
interface AddrHit { line1: string; city: string; state: string; zip: string; display: string }

interface ContactFormProps {
  open: boolean
  onClose: () => void
  contact?: Contact
  defaultAccountId?: string
}

/**
 * The form is mounted FRESH on every open, and again whenever the contact
 * changes underneath it (2026-09-29).
 *
 * Every field below is `useState(() => …contact…)` — captured once per MOUNT.
 * Both callers used to mount it permanently and toggle `open`, so the draft
 * was whatever it had been the first time: jump from one contact to another
 * (global search goes contact → contact without leaving the page) and Edit
 * showed the FIRST contact's values under the second one's name, and Save
 * wrote them onto the second contact. The same capture made a re-opened Edit
 * undo anything changed since (a spouse recorded, an address fixed), and made
 * a re-opened Add Contact start pre-filled with the last person created.
 * Rendering nothing while closed, and keying the body by contact id, makes
 * each open read the record as it is NOW.
 */
export function ContactForm(props: ContactFormProps) {
  if (!props.open) return null
  return <ContactFormBody key={props.contact?.id ?? 'new-contact'} {...props} />
}

function ContactFormBody({
  open, onClose, contact, defaultAccountId,
}: ContactFormProps) {
  const accounts = useStore((s) => s.data.accounts)
  const contacts = useStore((s) => s.data.contacts)
  const tracks = useStore((s) => s.data.tracks)
  const upsert = useStore((s) => s.upsert)
  const patchStore = useStore((s) => s.patch)
  const canManage = useHasPermission('crm.manage')
  const navigate = useNavigate()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const isEdit = !!contact

  const [d, setD] = useState<Draft>(() => toDraft(contact))
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((p) => ({ ...p, [k]: v }))
  const setAddr = (patch: Partial<Address>) => setD((p) => ({ ...p, officeAddress: { ...p.officeAddress, ...patch } }))

  /* account assignment */
  const initialAcctId = contact?.accountId ?? defaultAccountId
  const [acctMode, setAcctMode] = useState<AcctMode>(initialAcctId ? 'existing' : 'none')
  const [acctId, setAcctId] = useState<string | undefined>(initialAcctId)
  const [acctQuery, setAcctQuery] = useState(() => accounts.find((a) => a.id === initialAcctId)?.name ?? '')
  const [acctFocus, setAcctFocus] = useState(false)
  const [newAcctName, setNewAcctName] = useState('')

  /* relationship designation (role + primary), settable at creation */
  // The contact's membership in a track of `acct`. Role and track describe
  // the relationship with THAT account, so they are read for the account in
  // the field — never "the first membership, whichever account it is on":
  // Save wires the primary pointer onto this track, and a track left over
  // from another account put the contact back as primary THERE.
  const membershipFor = (acct?: string) => (acct
    ? contact?.trackMemberships?.find((m) =>
        tracks.some((t) => t.id === m.trackId && sameId(t.accountId, acct)))
    : undefined)
  const existingMembership = membershipFor(initialAcctId)
  const [relRole, setRelRole] = useState<string>(existingMembership?.role ?? '')
  const [relTrackId, setRelTrackId] = useState<string>(existingMembership?.trackId ?? '')
  // "Primary contact for THIS account" — the account in the field, and only
  // that one. It used to start ticked when the contact was primary of the
  // account the form opened on OR of any track anywhere, and nothing reset it
  // when the account changed, so moving a primary contact to another company
  // carried the tick across and Save made them primary of the new company
  // while the old one still named them — primary of two companies (2026-09-22,
  // found 2026-09-29). It now starts from the opening account and is
  // re-derived by retarget() on every change of the account field.
  const isPrimaryOf = (acct?: string): boolean =>
    !!contact && !!acct && sameId(accounts.find((a) => sameId(a.id, acct))?.primaryContactId, contact.id)
  const [setAsPrimary, setSetAsPrimary] = useState<boolean>(() => isPrimaryOf(initialAcctId))
  /** Every change of the account field goes through here: the tick follows
   *  the account now in the field — normally unticked, ticked only when the
   *  contact already IS that account's primary — and so do the role and the
   *  track, which belong to the old account's relationship otherwise. */
  const retarget = (next: string | undefined) => {
    setAcctId(next)
    setSetAsPrimary(isPrimaryOf(next))
    const m = membershipFor(next)
    setRelTrackId(m?.trackId ?? '')
    setRelRole(m?.role ?? '')
  }
  const accountTracks = useMemo(() => (acctId ? tracks.filter((t) => t.accountId === acctId) : []), [tracks, acctId])
  const willAssignAccount = (acctMode === 'existing' && !!acctId) || (acctMode === 'new' && !!newAcctName.trim())

  /* email-domain → account map (existing contacts + account websites) */
  const domainMap = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of contacts) {
      const dom = c.workEmail?.split('@')[1]?.toLowerCase()
      if (dom && c.accountId && !m.has(dom)) m.set(dom, c.accountId)
    }
    for (const a of accounts) {
      const dom = a.website?.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0].toLowerCase()
      if (dom && !m.has(dom)) m.set(dom, a.id)
    }
    return m
  }, [contacts, accounts])

  const emailDomain = d.workEmail?.split('@')[1]?.toLowerCase()
  const suggestedAcct = useMemo(() => {
    if (!emailDomain || acctId) return undefined
    const id = domainMap.get(emailDomain)
    return id ? accounts.find((a) => a.id === id) : undefined
  }, [emailDomain, acctId, domainMap, accounts])

  const acctMatches = useMemo(() => {
    const q = acctQuery.trim().toLowerCase()
    return accounts.filter((a) => a.name.toLowerCase().includes(q)).slice(0, 8)
  }, [acctQuery, accounts])

  /* address autocomplete (debounced Nominatim; degrades gracefully offline) */
  const [addrQuery, setAddrQuery] = useState('')
  const [addrHits, setAddrHits] = useState<AddrHit[]>([])
  const [addrLoading, setAddrLoading] = useState(false)
  const addrAbort = useRef<AbortController | null>(null)
  const skipLookup = useRef(false) // suppress one query after a suggestion is chosen
  useEffect(() => {
    if (skipLookup.current) { skipLookup.current = false; return }
    const q = addrQuery.trim()
    if (q.length < 4) { setAddrHits([]); return }
    const t = setTimeout(async () => {
      addrAbort.current?.abort()
      const ac = new AbortController(); addrAbort.current = ac
      setAddrLoading(true)
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&countrycodes=us&limit=5&q=${encodeURIComponent(q)}`,
          { signal: ac.signal, headers: { 'Accept-Language': 'en' } },
        )
        const json = await res.json()
        setAddrHits((json as { address?: Record<string, string>; display_name: string }[]).map((r) => {
          const a = r.address ?? {}
          return {
            line1: [a.house_number, a.road].filter(Boolean).join(' '),
            city: a.city || a.town || a.village || a.hamlet || a.suburb || a.county || '',
            state: abbr(a.state), zip: a.postcode || '', display: r.display_name,
          }
        }).filter((h) => h.line1 || h.city))
      } catch { /* offline or blocked — manual entry still works */ }
      finally { setAddrLoading(false) }
    }, 550)
    return () => clearTimeout(t)
  }, [addrQuery])

  const valid = (d.firstName ?? '').trim() && (d.lastName ?? '').trim()

  // Add Contact: is this person already here? Same email (work or personal)
  // or the same name in either order — lib/contactMatch.ts. Checked live, so
  // the warning is up before anything is saved; never in edit mode, where the
  // record being edited is the person.
  const existingMatches = useMemo(
    () => (isEdit ? [] : findExistingContacts(
      { firstName: d.firstName, lastName: d.lastName, workEmail: d.workEmail, personalEmail: d.personalEmail },
      contacts,
    )),
    [isEdit, d.firstName, d.lastName, d.workEmail, d.personalEmail, contacts],
  )
  /** The offered path when the person already exists: their record, not a
   *  second one. Nothing typed into this form is kept. */
  const openExisting = (id: string) => { onClose(); navigate(`/contacts/${id}`) }

  function clean<T extends object>(o: T): T {
    const out = {} as Record<string, unknown>
    for (const [k, v] of Object.entries(o)) {
      if (v === '' || v == null) continue
      if (Array.isArray(v)) { if (v.length) out[k] = v }
      else if (typeof v === 'object') { const inner = clean(v as object); if (Object.keys(inner).length) out[k] = inner }
      else out[k] = v
    }
    return out as T
  }

  function resolveAccountId(): string | undefined {
    if (acctMode === 'none') return undefined
    if (acctMode === 'existing') return acctId
    // new
    const nm = newAcctName.trim()
    if (!nm) return undefined
    // Same name already exists → link to it instead of minting a duplicate
    // (retry-after-error runs created "North Palisade Partners" ×3, 7/28).
    const match = accounts.find((a) => a.name.trim().toLowerCase() === nm.toLowerCase())
    if (match) return match.id
    let id = `acct-${slugify(nm)}`; let n = 2
    while (accounts.some((a) => a.id === id)) id = `acct-${slugify(nm)}-${n++}`
    const acct: Account = {
      id, name: nm, type: CONTACT_TO_ACCOUNT_TYPE[d.type as ContactType] ?? 'Personal',
      status: 'Prospect', trackIds: [],
    }
    upsert('accounts', acct)
    return id
  }

  function save(opts: { allowDuplicate?: boolean } = {}) {
    if (!valid) return
    // A new contact that looks like someone already here is created only
    // through the explicit "Create anyway" — the footer does not even offer
    // the ordinary button while the warning stands. (Belt and braces: this
    // is the one place a create actually happens.)
    if (!isEdit && existingMatches.length > 0 && !opts.allowDuplicate) return
    const accountId = resolveAccountId()
    const children = [d._child1, d._child2].map((x) => (x ?? '').trim()).filter(Boolean)
    const id = contact?.id ?? (() => {
      const base = `con-${slugify(`${d.firstName}-${d.lastName}`)}`
      let cand = base, n = 2
      while (contacts.some((c) => c.id === cand)) cand = `${base}-${n++}`
      return cand
    })()

    // relationship designation → track membership (role) + primary wiring
    const acctTracksNow = accountId ? tracks.filter((t) => t.accountId === accountId) : []
    const targetTrackId = relTrackId || (acctTracksNow.length === 1 ? acctTracksNow[0].id : '')
    const effectiveRole = relRole || (setAsPrimary ? 'Primary Contact' : '')
    let memberships = contact?.trackMemberships ?? []
    if (targetTrackId && effectiveRole) {
      const has = memberships.some((m) => m.trackId === targetTrackId)
      memberships = has
        ? memberships.map((m) => (m.trackId === targetTrackId
            ? { ...m, role: effectiveRole, ...(d.stance ? { stance: d.stance as Stance } : {}) } : m))
        : [...memberships, { trackId: targetTrackId, role: effectiveRole, ...(d.stance ? { stance: d.stance as Stance } : {}) }]
    }

    const next = clean({
      ...contact, id, accountId,
      firstName: d.firstName!.trim(), lastName: d.lastName!.trim(),
      title: d.title, department: d.department,
      type: d.type as ContactType, status: d.status as ContactStatus,
      country: d.country, workEmail: d.workEmail, personalEmail: d.personalEmail,
      officePhone: d.officePhone, cellPhone: d.cellPhone,
      officeAddress: d.officeAddress, sameAsHQ: d.sameAsHQ,
      birthday: d.birthday, holiday: d.holiday as Holiday, hobby: d.hobby,
      sourceReferredBy: d.sourceReferredBy, almaMater: d.almaMater, degree: d.degree,
      married: d.married, spouse: d.spouse, children, g2: d.g2,
      investorSymposium: d.investorSymposium, influence: d.influence as InfluenceLevel,
      shirtSize: d.shirtSize, shirtStyle: d.shirtStyle,
      stance: d.stance as Stance, description: d.description, social: d.social,
    }) as Contact
    next.trackMemberships = memberships // always an array (and not stripped by clean)
    // Archived state changes ONLY via the dedicated Archive/Unarchive controls
    // — carrying it here would 403 the whole edit for non-crm.manage users
    // (the API blocks any write whose payload touches `archived`).
    delete (next as Partial<Contact>).archived
    upsert('contacts', next)

    // Moved to another account (or to none): the OLD account must stop naming
    // them as its primary — the other half of the two-companies defect (see
    // isPrimaryOf). Cleared through the same write path this form uses to SET
    // a primary, and only where the pointer really is this contact.
    const prevAcctId = contact?.accountId
    if (isEdit && prevAcctId && !sameId(prevAcctId, accountId)) {
      const prev = accounts.find((a) => sameId(a.id, prevAcctId))
      if (prev && sameId(prev.primaryContactId, id)) {
        // '' and not undefined (JSON drops undefined, so nothing would reach
        // the server). The API stores '' as "a person cleared it", which also
        // beats the prototype seed's primary on the four seed client accounts
        // — a NULL there is silently refilled from the seed on every refresh.
        patchStore('accounts', prev.id, { primaryContactId: '' })
      }
      for (const t of tracks) {
        if (sameId(t.accountId, prevAcctId) && sameId(t.primaryContactId, id)) {
          // null rather than '': the account's Contacts tab falls back to the
          // account's own primary when a track names nobody (`??`), and ''
          // would block that fallback. Tracks are document-store rows, so the
          // null is kept as written.
          patchStore('tracks', t.id, { primaryContactId: null } as unknown as Partial<RelationshipTrack>)
        }
      }
    }

    // ensure the contact shows under the track, and set primary where requested
    if (accountId && targetTrackId) {
      const tr = tracks.find((t) => t.id === targetTrackId)
      if (tr && !tr.counterpartyContactIds.includes(id)) {
        patchStore('tracks', targetTrackId, { counterpartyContactIds: [...tr.counterpartyContactIds, id] })
      }
      if (setAsPrimary) patchStore('tracks', targetTrackId, { primaryContactId: id })
    }
    if (accountId && setAsPrimary) patchStore('accounts', accountId, { primaryContactId: id })
    onClose()
  }

  function pickAccount(a: Account) { retarget(a.id); setAcctQuery(a.name); setAcctFocus(false) }

  return (
    <Modal
      open={open} onClose={onClose} width={740}
      title={isEdit ? <>Edit <b>{contact!.firstName} {contact!.lastName}</b></> : <>New <b>Contact</b></>}
      footer={<>
        {/* No hard delete — contacts are ARCHIVED (Charles's rule): hidden
            from everyone except admins, restorable from the Archived view. */}
        {isEdit && canManage && !contact!.archived && (
          <span className="cform-delete">
          <Button
            variant="ghost"
            onClick={() => {
              if (!confirmDelete) { setConfirmDelete(true); return }
              patchStore('contacts', contact!.id, { archived: true })
              onClose()
              navigate('/contacts')
            }}
          >
            {confirmDelete ? 'Archive this contact? Admins can restore them anytime' : 'Archive contact'}
          </Button>
          </span>
        )}
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        {existingMatches.length > 0 ? (
          // The person may already exist: opening them is the offered path,
          // and creating a second record takes an explicit, differently-
          // worded click. With several look-alikes the list above carries one
          // Open per record instead of the footer guessing which is meant.
          <>
            <Button variant="default" onClick={() => save({ allowDuplicate: true })} disabled={!valid}>
              Create anyway
            </Button>
            {existingMatches.length === 1 && (
              <Button
                variant="primary" icon={<ExternalLink size={14} />}
                onClick={() => openExisting(existingMatches[0].contact.id)}
              >
                Open existing
              </Button>
            )}
          </>
        ) : (
          <Button variant="primary" onClick={() => save()} disabled={!valid}>{isEdit ? 'Save changes' : 'Create contact'}</Button>
        )}
      </>}
    >
      <div className="cform">
        {existingMatches.length > 0 && (
          <div className="cform-dupes" role="alert">
            <p className="cform-dupes-head">
              <AlertTriangle size={15} />
              <span>
                <b>
                  {existingMatches.length === 1
                    ? 'This person may already be in the CRM.'
                    : `${existingMatches.length} existing contacts look like this person.`}
                </b>{' '}
                Open the existing record rather than creating a second one — nothing typed here is
                saved if you do. Use <b>Create anyway</b> only for a different person.
              </span>
            </p>
            <ul className="cform-dupes-list">
              {existingMatches.map((m) => {
                const acct = accounts.find((a) => sameId(a.id, m.contact.accountId))
                const email = m.contact.workEmail || m.contact.personalEmail
                const why = [
                  m.reasons.includes('email') ? `same email (${m.sharedEmails.join(', ')})` : '',
                  m.reasons.includes('name') ? 'same name' : '',
                  m.contact.archived ? 'archived' : '',
                ].filter(Boolean).join(' · ')
                return (
                  <li key={m.contact.id} className="cform-dupe">
                    <div className="cform-dupe-main">
                      <strong>{contactName(m.contact)}</strong>
                      <span className="cform-dupe-sub">
                        {acct?.name ?? (m.contact.isWpi ? 'WPI staff' : 'No account')}
                        {email ? ` · ${email}` : ''}
                      </span>
                      <span className="cform-dupe-why">{why}</span>
                    </div>
                    <Button
                      size="sm" variant={existingMatches.length === 1 ? 'default' : 'primary'}
                      icon={<ExternalLink size={13} />}
                      onClick={() => openExisting(m.contact.id)}
                    >
                      Open
                    </Button>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
        <div className="cform-sec-label">Identity</div>
        <FormGrid>
          <Field label="First Name" required><TextInput value={d.firstName ?? ''} onChange={(e) => set('firstName', e.target.value)} /></Field>
          <Field label="Last Name" required><TextInput value={d.lastName ?? ''} onChange={(e) => set('lastName', e.target.value)} /></Field>
          <Field label="Title"><TextInput value={d.title ?? ''} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Managing Director" /></Field>
          <Field label="Department"><TextInput value={d.department ?? ''} onChange={(e) => set('department', e.target.value)} placeholder="Business line" /></Field>
          <Field label="Type">
            <Select value={d.type ?? ''} onChange={(e) => set('type', e.target.value as ContactType)}>
              {CONTACT_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={d.status ?? ''} onChange={(e) => set('status', e.target.value as ContactStatus)}>
              {CONTACT_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Source / Referred By" span={2}><TextInput value={d.sourceReferredBy ?? ''} onChange={(e) => set('sourceReferredBy', e.target.value)} /></Field>
        </FormGrid>

        {/* ---- Account assignment ---- */}
        <div className="cform-sec-label">Account</div>
        <div className="cform-acct">
          <div className="cform-modes">
            {(['none', 'existing', 'new'] as AcctMode[]).map((m) => (
              <button key={m} type="button" className={`cform-mode${acctMode === m ? ' active' : ''}`}
                onClick={() => { setAcctMode(m); if (m !== 'existing') { retarget(undefined) } }}>
                {m === 'none' ? 'No account' : m === 'existing' ? 'Existing account' : 'New account'}
              </button>
            ))}
          </div>

          {acctMode === 'existing' && (
            <div className="cform-typeahead">
              <Search size={15} className="cform-ta-icon" />
              <input className="input cform-ta-input" placeholder="Search accounts…" value={acctQuery}
                onChange={(e) => { setAcctQuery(e.target.value); retarget(undefined); setAcctFocus(true) }}
                onFocus={() => setAcctFocus(true)} onBlur={() => setTimeout(() => setAcctFocus(false), 150)} />
              {acctId && <Check size={15} className="cform-ta-check" />}
              {acctFocus && acctMatches.length > 0 && !acctId && (
                <div className="cform-ta-menu">
                  {acctMatches.map((a) => (
                    <button key={a.id} type="button" className="cform-ta-opt" onMouseDown={() => pickAccount(a)}>
                      <Building2 size={14} /><span>{a.name}</span><span className="cform-ta-type">{a.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {acctMode === 'new' && (
            <>
              <TextInput placeholder="New account name (created on save)" value={newAcctName} onChange={(e) => setNewAcctName(e.target.value)} />
              {(() => {
                const nm = newAcctName.trim().toLowerCase()
                const match = nm ? accounts.find((a) => a.name.trim().toLowerCase() === nm) : undefined
                return match
                  ? <p className="cform-hint"><b>{match.name}</b> already exists — this contact will be linked to it.</p>
                  : null
              })()}
            </>
          )}
          {acctMode === 'none' && <p className="cform-hint">This contact won’t be tied to an account. You can assign one later.</p>}

          {suggestedAcct && acctMode !== 'new' && (
            <button type="button" className="cform-suggest" onMouseDown={() => { setAcctMode('existing'); pickAccount(suggestedAcct) }}>
              <Sparkles size={14} /> Matched by email domain — assign to <b>{suggestedAcct.name}</b>?
            </button>
          )}

          <div className="cform-rel">
            <FormGrid>
              <Field label="Relationship Role" hint={willAssignAccount ? 'How they fit the relationship' : 'Assign an account to enable'}>
                <Select value={relRole} disabled={!willAssignAccount} onChange={(e) => setRelRole(e.target.value)}>
                  <option value="">—</option>
                  {REL_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </Select>
              </Field>
              {willAssignAccount && accountTracks.length > 1 && (
                <Field label="Track" hint="Which track this contact anchors">
                  <Select value={relTrackId || accountTracks[0].id} onChange={(e) => setRelTrackId(e.target.value)}>
                    {accountTracks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </Select>
                </Field>
              )}
            </FormGrid>
            <label className={`cform-check cform-primary-check${willAssignAccount ? '' : ' is-disabled'}`}>
              <input type="checkbox" disabled={!willAssignAccount} checked={setAsPrimary} onChange={(e) => setSetAsPrimary(e.target.checked)} />
              <Star size={14} /> Set as <b>primary contact</b> for this account
            </label>
          </div>
        </div>

        <div className="cform-sec-label">Contact</div>
        <FormGrid>
          <Field label="Work Email"><TextInput type="email" value={d.workEmail ?? ''} onChange={(e) => set('workEmail', e.target.value)} placeholder="name@firm.com" /></Field>
          <Field label="Personal Email"><TextInput type="email" value={d.personalEmail ?? ''} onChange={(e) => set('personalEmail', e.target.value)} /></Field>
          <Field label="Office Phone"><TextInput inputMode="tel" value={d.officePhone ?? ''} onChange={(e) => set('officePhone', formatPhone(e.target.value))} placeholder="(949) 748-5900" /></Field>
          <Field label="Cell Phone"><TextInput inputMode="tel" value={d.cellPhone ?? ''} onChange={(e) => set('cellPhone', formatPhone(e.target.value))} /></Field>

          <Field label="Street" span={2} hint="Start typing — OpenStreetMap autocomplete (needs a connection); all fields stay editable.">
            <div className="cform-typeahead">
              <MapPin size={15} className="cform-ta-icon" />
              <input className="input cform-ta-input" placeholder="Start typing a street address…"
                value={d.officeAddress?.line1 ?? ''}
                onChange={(e) => { setAddr({ line1: e.target.value }); setAddrQuery(e.target.value) }}
                onBlur={() => setTimeout(() => setAddrHits([]), 150)} />
              {addrLoading && <Loader2 size={15} className="cform-ta-spin" />}
              {addrHits.length > 0 && (
                <div className="cform-ta-menu">
                  {addrHits.map((h, i) => (
                    <button key={i} type="button" className="cform-ta-opt cform-ta-opt--addr"
                      onMouseDown={() => { skipLookup.current = true; setAddr({ line1: h.line1, city: h.city, state: h.state, zip: h.zip }); setAddrQuery(h.line1); setAddrHits([]) }}>
                      <MapPin size={13} /><span className="truncate">{h.display}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Field>
          <Field label="Suite / Unit"><TextInput value={d.officeAddress?.line2 ?? ''} onChange={(e) => setAddr({ line2: e.target.value })} placeholder="Suite 400" /></Field>
          <Field label="City"><TextInput value={d.officeAddress?.city ?? ''} onChange={(e) => setAddr({ city: e.target.value })} /></Field>
          <Field label="State"><TextInput value={d.officeAddress?.state ?? ''} onChange={(e) => setAddr({ state: e.target.value })} placeholder="CA" /></Field>
          <Field label="ZIP"><TextInput value={d.officeAddress?.zip ?? ''} onChange={(e) => setAddr({ zip: e.target.value })} /></Field>
          <Field label="Country"><TextInput value={d.country ?? ''} onChange={(e) => set('country', e.target.value)} placeholder="USA" /></Field>
          <Field label="Same as HQ?">
            <label className="cform-check"><input type="checkbox" checked={!!d.sameAsHQ} onChange={(e) => set('sameAsHQ', e.target.checked)} /> Office same as account HQ</label>
          </Field>
        </FormGrid>

        <div className="cform-sec-label">Personal &amp; Relationship</div>
        <FormGrid>
          <Field label="Birthday"><TextInput type="date" value={d.birthday ?? ''} onChange={(e) => set('birthday', e.target.value)} /></Field>
          <Field label="Holiday">
            <Select value={d.holiday ?? ''} onChange={(e) => set('holiday', e.target.value as Holiday)}>
              <option value="">—</option>{HOLIDAY_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
            </Select>
          </Field>
          <Field label="Hobby / Interest"><TextInput value={d.hobby ?? ''} onChange={(e) => set('hobby', e.target.value)} /></Field>
          <Field label="Alma Mater" hint="Type to search — or enter your own">
            <TextInput list="cform-universities" value={d.almaMater ?? ''} onChange={(e) => set('almaMater', e.target.value)} placeholder="Start typing a school…" />
            <datalist id="cform-universities">{UNIVERSITIES.map((u) => <option key={u} value={u} />)}</datalist>
          </Field>
          <Field label="Degree"><TextInput value={d.degree ?? ''} onChange={(e) => set('degree', e.target.value)} placeholder="e.g. MBA, Finance" /></Field>
          <Field
            label="WPI Investor Symposium"
            hint={d.investorSymposium === true
              ? 'Adds them to this year\u2019s invitation list for review \u2014 no email is sent.'
              : 'Yes adds them to this year\u2019s invitation list for review.'}
          >
            <Select value={d.investorSymposium == null ? '' : d.investorSymposium ? 'yes' : 'no'} onChange={(e) => set('investorSymposium', e.target.value === '' ? undefined : e.target.value === 'yes')}>
              <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>
            </Select>
          </Field>
          <Field label="Shirt Size" hint="For event gifts">
            <Select value={d.shirtSize ?? ''} onChange={(e) => set('shirtSize', e.target.value || undefined)}>
              <option value="">—</option>
              {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Shirt Style">
            <Select value={d.shirtStyle ?? ''} onChange={(e) => set('shirtStyle', (e.target.value || undefined) as Contact['shirtStyle'])}>
              <option value="">—</option><option value="M">Men’s</option><option value="W">Women’s</option>
            </Select>
          </Field>
          <Field label="Married">
            <label className="cform-check"><input type="checkbox" checked={!!d.married} onChange={(e) => set('married', e.target.checked)} /> Married</label>
          </Field>
          <Field label="Spouse"><TextInput value={d.spouse ?? ''} onChange={(e) => set('spouse', e.target.value)} /></Field>
          <Field label="Child"><TextInput value={d._child1 ?? ''} onChange={(e) => set('_child1', e.target.value)} /></Field>
          <Field label="Child"><TextInput value={d._child2 ?? ''} onChange={(e) => set('_child2', e.target.value)} /></Field>
          <Field label="G2" hint="Next-generation / family-office contact"><TextInput value={d.g2 ?? ''} onChange={(e) => set('g2', e.target.value)} /></Field>
          <Field label="Influence">
            <Select value={d.influence ?? ''} onChange={(e) => set('influence', e.target.value as InfluenceLevel)}>
              <option value="">—</option>{INFLUENCES.map((i) => <option key={i} value={i}>{i}</option>)}
            </Select>
          </Field>
          <Field label="Stance">
            <Select value={d.stance ?? ''} onChange={(e) => set('stance', e.target.value as Stance)}>
              <option value="">—</option>{STANCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
        </FormGrid>

        <div className="cform-sec-label">Socials</div>
        <FormGrid>
          {SOCIALS.map((s) => (
            <Field key={s.key} label={s.label}>
              <div className="cform-social">
                <span className="cform-social-prefix">{s.prefix}</span>
                <input className="cform-social-input" value={toHandle(s.prefix, d.social?.[s.key])}
                  onChange={(e) => setD((p) => ({ ...p, social: { ...p.social, [s.key]: toUrl(s.prefix, e.target.value) } }))}
                  placeholder={s.prefix.includes('@') ? 'handle' : 'handle'} />
              </div>
            </Field>
          ))}
        </FormGrid>

        <div className="cform-sec-label">Description</div>
        <FormGrid>
          <Field label="Notes / Description" span={2}>
            <TextArea value={d.description ?? ''} onChange={(e) => set('description', e.target.value)} placeholder="Background, relationship context, talking points…" />
          </Field>
        </FormGrid>
      </div>
    </Modal>
  )
}
